import { useState, type FC } from 'react';

import { useContent } from '@eneco/cms';
import { type Link, linkWithUrl } from '@eneco/cms-types';
import {
  Dialog,
  NavigationMenu,
  NavigationMenuWrapper,
  StyledDialogContentMobile,
  StyledNavigationMenuContentDesktop,
  StyledNavigationMenuTriger,
  StyledTrigger,
} from '@eneco/custom-components/MegaMenu';
import { Box, Divider, Grid, NavLink as SparkyNavLink, PageGrid, Stack, Stretch, Text, VisuallyHidden } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';

import { getIllustration } from './illustrations';
import type { MegaMenuV2Content } from './mega-menu-v2-content.types';
import type { NavigationMenuItem } from './navigation-menu-content.types';

/**
 * `MegaMenuV2` block for the CMS-agnostic lane, serving **both** providers. Its navigation lives on the
 * referenced `siteWideContent` document, whose `items` are a mix of `navItem` (one link) and `navGroup`
 * (a heading over its own items).
 *
 * Built on the same `@eneco/custom-components/MegaMenu` primitives as the legacy component
 * (`libs/sitecore/components/content/src/MegaMenuV2/MegaMenuV2.tsx`): a Radix `NavigationMenu` with
 * hover-opened dropdowns on desktop, a `Dialog` on mobile. `lib:cms-components` was widened to allow
 * `lib:custom-components` for this — see `eslint.config.mjs`.
 *
 * Simpler than the legacy division in two ways, because the migrated content is simpler: a group's
 * items carry an illustration and a link but no teaser text, and no group holds a `ContentCard`. Both
 * are additive if that content appears later.
 *
 * The document's own `title` is deliberately not rendered — it is the editor's label for the menu
 * ("Mega menu"), and the legacy component never shows it either.
 */
const MegaMenuV2: FC = () => {
  const content = useContent<MegaMenuV2Content>();
  const isDesktop = useMediaQuery('lg');

  const entries = toEntries(content.siteWideContent?.items);
  if (entries.length === 0) return null;

  return isDesktop ? <DesktopMenu entries={entries} /> : <MobileMenu entries={entries} />;
};

const DesktopMenu: FC<{ entries: Entry[] }> = ({ entries }) => (
  <NavigationMenuWrapper>
    <NavigationMenu.Root>
      <NavigationMenu.List asChild>
        <Stack as="ul" direction="row" gap="6" alignY="center">
          {entries.map(entry => (
            <NavigationMenu.Item key={entry.key}>
              {entry.kind === 'group' ? <DesktopGroup group={entry} /> : <NavItemLink entry={entry} />}
            </NavigationMenu.Item>
          ))}
        </Stack>
      </NavigationMenu.List>
    </NavigationMenu.Root>
  </NavigationMenuWrapper>
);

const DesktopGroup: FC<{ group: GroupEntry }> = ({ group }) => (
  <>
    <StyledNavigationMenuTriger>
      {group.heading}
      <ChevronDownIcon color="iconOnBackgroundVarFive" />
    </StyledNavigationMenuTriger>
    <StyledNavigationMenuContentDesktop>
      <Box paddingY="16" backgroundColor="backgroundPrimary">
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            <Grid as="ul" rowGap="10" columnGap="6" gridTemplateColumns={{ initial: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }}>
              {group.links.map(link => (
                <Grid.Item as="li" key={link.key}>
                  <NavItemLink entry={link} />
                </Grid.Item>
              ))}
            </Grid>
          </PageGrid.Item>
        </PageGrid>
      </Box>
    </StyledNavigationMenuContentDesktop>
  </>
);

const MobileMenu: FC<{ entries: Entry[] }> = ({ entries }) => (
  <Stack as="ul" gap="5">
    {entries.map(entry => (
      <Stack.Item as="li" key={entry.key}>
        {entry.kind === 'group' ? <MobileGroup group={entry} /> : <NavItemLink entry={entry} />}
      </Stack.Item>
    ))}
  </Stack>
);

/**
 * A group as a mobile dialog. `modal={false}` matches the legacy component: the header stays usable
 * behind the panel, and it does not trap focus on a menu the user can dismiss by navigating.
 */
const MobileGroup: FC<{ group: GroupEntry }> = ({ group }) => {
  const [isOpen, setOpen] = useState(false);
  const toggle = () => setOpen(open => !open);

  return (
    <Dialog.Root modal={false} open={isOpen} onOpenChange={setOpen}>
      <StyledTrigger>
        {group.heading}
        <ChevronRightIcon />
      </StyledTrigger>
      <StyledDialogContentMobile>
        <Stretch>
          <Box overflow="auto" paddingY="5" paddingX={{ initial: '5', sm: '10' }} backgroundColor="backgroundPrimary">
            <Stack gap="6">
              {/*
                Radix requires a `Dialog.Title` for screen-reader users and warns without one. The panel
                already shows the heading on its back button, so the accessible title is hidden rather
                than duplicated. The legacy component omits it entirely — not reproduced here.
              */}
              <Dialog.Title asChild>
                <VisuallyHidden>{group.heading}</VisuallyHidden>
              </Dialog.Title>
              <Stack.Item>
                <button type="button" onClick={toggle}>
                  <Stack direction="row" alignY="center" gap="2">
                    <ChevronLeftIcon color="currentColor" />
                    <Text size="BodyL">{group.heading}</Text>
                  </Stack>
                </button>
              </Stack.Item>
              <Divider />
              <Stack.Item>
                <Stack as="ul" gap="5">
                  {group.links.map(link => (
                    <Stack.Item as="li" key={link.key}>
                      <NavItemLink entry={link} />
                    </Stack.Item>
                  ))}
                </Stack>
              </Stack.Item>
            </Stack>
          </Box>
        </Stretch>
      </StyledDialogContentMobile>
    </Dialog.Root>
  );
};

/**
 * One resolved `navItem`, with its illustration when it has one.
 *
 * `icon` names an **illustration**, not an icon — the legacy division resolves the same field through
 * `getIllustration`, and Sparky's icon set has no `Chart`/`SolarPanel`.
 *
 * The href is the last-resort label. It is reached only when the link has neither its own `text` nor a
 * target page whose `browserTitle` could stand in (`mappers.ts#referenceLabel`), which for GB content
 * means an internal link the migration left unresolved.
 */
const NavItemLink: FC<{ entry: LinkEntry }> = ({ entry: { link, icon } }) => {
  const Illustration = getIllustration(icon);

  return (
    <Stack direction="row" alignY="center" gap="3">
      {Illustration && (
        <Stack.Item>
          <Illustration size="small" color="iconSecondary" />
        </Stack.Item>
      )}
      <Stack.Item shrink={false}>
        <SparkyNavLink href={link.href} target={link.target}>
          <Text size="BodyL">{link.text ?? link.href}</Text>
        </SparkyNavLink>
      </Stack.Item>
    </Stack>
  );
};

type LinkEntry = { kind: 'link'; key: string; link: Link; icon?: string };
type GroupEntry = { kind: 'group'; key: string; heading?: string; links: LinkEntry[] };
type Entry = LinkEntry | GroupEntry;

/**
 * Resolve the menu into what can actually be rendered, dropping the rest.
 *
 * Resolution happens here rather than inside the leaf component so an item with no destination never
 * reaches the markup at all. Rendering it and returning `null` from the child still emits the `<li>`
 * wrapper, which is what put empty rows under a group heading.
 *
 * An item resolves to nothing when its link has no URL, such as an internal link without a page.
 */
const toEntries = (items: NavigationMenuItem[] | undefined): Entry[] =>
  (items ?? []).flatMap((item, index): Entry[] => {
    const key = item._key ?? String(index);

    if (item._type === 'navGroup') {
      const links = toEntries(item.items).filter((entry): entry is LinkEntry => entry.kind === 'link');
      if (links.length === 0 && !item.heading) return [];
      return [{ kind: 'group', key, links, ...(item.heading ? { heading: item.heading } : {}) }];
    }

    const link = linkWithUrl(item.link);
    return link ? [{ kind: 'link', key, link, ...(item.icon ? { icon: item.icon } : {}) }] : [];
  });

export default MegaMenuV2;
